import { z } from "zod";
import { planSourceQuantitiesSchema } from "./production/plan-source-quantities.js";

export const WEEKLY_PLANNING_API_BASE = "/api/weekly-planning";

const dateSchema = z.iso.date();
const instantSchema = z.iso.datetime({ offset: true });
const nullableText = (maximum: number) => z.string().trim().min(1).max(maximum).nullable();
const nullableQuantity = z.number().finite().positive().max(1_000_000_000_000).nullable();
const nullableCapacity = z.number().finite().min(0.000001).max(1_000_000_000).nullable();
const nullableDuration = z.number().finite().min(0.000001).max(168).nullable();
const optionalNullableText = (maximum: number) => z.string().trim().min(1).max(maximum).nullable().optional();

export const weeklyPlanUnitSchema = z.enum(["kg", "koli", "adet"]);
export type WeeklyPlanUnit = z.infer<typeof weeklyPlanUnitSchema>;

export const weeklyPlanRowKindSchema = z.enum(["PRODUCTION", "ACTIVITY"]);
export type WeeklyPlanRowKind = z.infer<typeof weeklyPlanRowKindSchema>;

export const weeklyPlanRowStateSchema = z.enum(["READY", "INCOMPLETE", "DEPENDENCY_BLOCKED"]);
export type WeeklyPlanRowState = z.infer<typeof weeklyPlanRowStateSchema>;

export const weeklyPlanMissingFieldSchema = z.enum([
  "lineId", "productCode", "quantity", "unit", "capacityPerHour", "durationHours", "sourceValidation", "workOrderIdentity",
]);
export type WeeklyPlanMissingField = z.infer<typeof weeklyPlanMissingFieldSchema>;

export const weeklyPlanStatusSchema = z.enum(["DRAFT", "ACTIVE", "SUPERSEDED"]);
export type WeeklyPlanStatus = z.infer<typeof weeklyPlanStatusSchema>;

export const weeklyPlanExecutionModeSchema = z.enum(["CAPACITY_SCHEDULED", "OPERATOR_SELECTED"]);
export type WeeklyPlanExecutionMode = z.infer<typeof weeklyPlanExecutionModeSchema>;

export const weeklyPlanDayStartSchema = z.enum(["00:00", "08:00"]);
export type WeeklyPlanDayStart = z.infer<typeof weeklyPlanDayStartSchema>;

export const weeklyPlanRowInputSchema = z.object({
  id: z.string().uuid().optional(),
  kind: weeklyPlanRowKindSchema,
  lineId: z.string().trim().min(1).max(120).nullable(),
  sequence: z.number().int().positive().max(10_000),
  workOrderNo: nullableText(80),
  productCode: nullableText(80),
  productName: z.string().trim().min(1).max(240),
  quantity: nullableQuantity,
  unit: weeklyPlanUnitSchema.nullable(),
  capacityPerHour: nullableCapacity,
  durationHours: nullableDuration,
  note: nullableText(2_000),
  carryoverFromRowId: z.string().uuid().nullable().optional(),
}).superRefine((row, context) => {
  if (row.kind === "PRODUCTION") {
    if (row.durationHours != null) context.addIssue({ code: "custom", path: ["durationHours"], message: "Üretim süresi miktar ve kapasiteden hesaplanır." });
    if ((row.unit === "koli" || row.unit === "adet") && row.quantity != null && !Number.isInteger(row.quantity)) {
      context.addIssue({ code: "custom", path: ["quantity"], message: "Koli ve adet miktarı tam sayı olmalıdır." });
    }
    if (row.quantity != null && row.capacityPerHour != null && row.quantity / row.capacityPerHour > 100_000) {
      context.addIssue({ code: "custom", path: ["capacityPerHour"], message: "Miktar ve kapasite güvenli planlama sınırını aşıyor." });
    }
  } else {
    for (const [field, value] of [
      ["productCode", row.productCode],
      ["quantity", row.quantity],
      ["unit", row.unit],
      ["capacityPerHour", row.capacityPerHour],
      ["workOrderNo", row.workOrderNo],
    ] as const) {
      if (value != null) context.addIssue({ code: "custom", path: [field], message: "Etkinlik satırında boş olmalıdır." });
    }
  }
});
export type WeeklyPlanRowInput = z.infer<typeof weeklyPlanRowInputSchema>;

export const weeklyPlanRowSchema = z.object({
  id: z.string().uuid(),
  executionKey: z.string().min(1).max(120),
  kind: weeklyPlanRowKindSchema,
  lineId: z.string().min(1).max(120).nullable(),
  lineName: z.string().min(1),
  sequence: z.number().int().positive(),
  workOrderNo: z.string().nullable(),
  productCode: z.string().nullable(),
  productName: z.string(),
  quantity: z.number().positive().nullable(),
  unit: weeklyPlanUnitSchema.nullable(),
  capacityPerHour: z.number().positive().nullable(),
  durationHours: z.number().positive().nullable(),
  state: weeklyPlanRowStateSchema.default("READY"),
  missingFields: z.array(weeklyPlanMissingFieldSchema).default([]),
  plannedHours: z.number().positive().nullable(),
  scheduledHours: z.number().nonnegative().nullable(),
  scheduledQuantity: z.number().nonnegative().nullable(),
  deferredQuantity: z.number().nonnegative().nullable(),
  scheduledStartAt: instantSchema.nullable(),
  scheduledEndAt: instantSchema.nullable(),
  note: z.string().nullable(),
  carryoverFromRowId: z.string().uuid().nullable(),
  source: z.object({
    kind: z.literal("EXCEL"),
    fileName: z.string(),
    fileHash: z.string().regex(/^[a-f0-9]{64}$/u),
    sheetName: z.string(),
    sourceRow: z.number().int().positive(),
    sourceLineName: z.string(),
    sourceCells: z.array(z.string()),
    quantities: planSourceQuantitiesSchema.optional(),
    businessIdentity: z.object({
      salesOrderNo: optionalNullableText(80),
      customerCode: optionalNullableText(80),
      customerName: optionalNullableText(240),
      country: optionalNullableText(120),
    }).optional(),
    sourceIssues: z.array(z.string()),
    blockingSourceIssues: z.array(z.object({
      message: z.string(),
      editableField: z.enum(["quantity", "capacityPerHour", "durationHours"]).nullable(),
    })).default([]),
    workOrderVerification: z.object({
      observedAt: instantSchema,
      match: z.enum(["EXCEL", "MIKRO_PRODUCT_UNIQUE", "MIKRO_BULK_Q", "MISSING", "AMBIGUOUS"]),
      resolvedWorkOrderNo: z.string().min(1).max(80).nullable(),
      verifiedProductCode: z.string().min(1).max(80),
      explicitProductMatched: z.boolean(),
      candidateSource: z.enum(["EXCEL", "PRODUCT_EXACT", "Q_BULK", "PLANNER_CORRECTION", "NONE"]),
      mikroIdentity: z.object({
        workOrderNo: z.string().min(1).max(80),
        productCode: z.string().min(1).max(80),
        fullWorkOrderQuantity: z.number().finite().nonnegative().nullable(),
        unit: optionalNullableText(40),
        salesOrderNo: optionalNullableText(80),
        salesOrderLine: z.number().int().nonnegative().nullable().optional(),
        salesQuantity: z.number().finite().nonnegative().nullable().optional(),
        customerCode: optionalNullableText(80),
        customerName: optionalNullableText(240),
        deliveryCountry: optionalNullableText(120),
      }).optional(),
      correction: z.object({
        verifiedAt: instantSchema,
        verifiedBy: z.string().min(1).max(160),
        previousWorkOrderNo: z.string().min(1).max(80).nullable(),
        correctedWorkOrderNo: z.string().min(1).max(80),
        identityScope: z.literal("WORK_ORDER_PRODUCT_ORDER_CUSTOMER"),
        salesOrderMatch: z.enum(["MATCH", "CORRECTED", "UNKNOWN"]),
        customerMatch: z.enum(["MATCH", "CORRECTED", "UNKNOWN"]),
        countryMatch: z.enum(["MATCH", "MISMATCH", "UNKNOWN"]),
        correctionReason: optionalNullableText(400),
      }).optional(),
    }).nullable().default(null),
    suggestedDurationHours: z.number().positive().nullable(),
    originalDayStartsAt: weeklyPlanDayStartSchema,
    originalKind: weeklyPlanRowKindSchema,
    originalLineId: z.string().nullable(),
    originalProductName: z.string(),
    originalSequence: z.number().int().positive(),
    originalWorkOrderNo: z.string().nullable(),
    originalProductCode: z.string().nullable(),
    originalQuantity: z.number().positive().nullable(),
    originalUnit: weeklyPlanUnitSchema.nullable(),
    originalCapacityPerHour: z.number().positive().nullable(),
    originalDurationHours: z.number().positive().nullable(),
    originalNote: z.string().nullable(),
    originalScheduledStartAt: instantSchema.nullable(),
    originalScheduledEndAt: instantSchema.nullable(),
    originalScheduledHours: z.number().nonnegative().nullable(),
    originalScheduledQuantity: z.number().nonnegative().nullable(),
    originalDeferredQuantity: z.number().nonnegative().nullable(),
    originalAllocations: z.array(z.object({
      requiredAt: dateSchema,
      quantity: z.number().nonnegative(),
      plannedHours: z.number().positive(),
      startOffsetHours: z.number().nonnegative(),
    })).default([]),
  }).nullable().default(null),
});
export type WeeklyPlanRow = z.infer<typeof weeklyPlanRowSchema>;

const editablePlanFields = {
  title: z.string().trim().min(1).max(160).optional(),
  note: z.string().trim().min(1).max(4_000).nullable().optional(),
  dayStartsAt: weeklyPlanDayStartSchema.default("08:00"),
  executionMode: weeklyPlanExecutionModeSchema.default("CAPACITY_SCHEDULED"),
  rows: z.array(weeklyPlanRowInputSchema).max(1_000).default([]),
};

export const createWeeklyPlanRequestSchema = z.object({
  weekStart: dateSchema,
  basePlanId: z.string().uuid().optional(),
  ...editablePlanFields,
  carryoverRowIds: z.array(z.string().uuid()).max(1_000).default([]),
});
export type CreateWeeklyPlanRequest = z.infer<typeof createWeeklyPlanRequestSchema>;

export const saveWeeklyPlanRequestSchema = z.object({
  expectedRevision: z.number().int().positive(),
  ...editablePlanFields,
  carryoverRowIds: z.array(z.string().uuid()).max(1_000).default([]),
});
export type SaveWeeklyPlanRequest = z.infer<typeof saveWeeklyPlanRequestSchema>;

export const verifyWeeklyPlanWorkOrderRequestSchema = z.object({
  expectedRevision: z.number().int().positive(),
  workOrderNo: z.string().trim().min(1).max(80),
  salesOrderNo: nullableText(80),
  customerCode: nullableText(80),
  correctionReason: nullableText(400),
});
export type VerifyWeeklyPlanWorkOrderRequest = z.infer<typeof verifyWeeklyPlanWorkOrderRequestSchema>;

export const activateWeeklyPlanRequestSchema = z.object({
  expectedRevision: z.number().int().positive(),
  idempotencyKey: z.string().trim().min(8).max(120),
  comparisonDigest: z.string().regex(/^[a-f0-9]{64}$/u),
  acknowledgedExcludedRowIds: z.array(z.string().uuid()).max(1_000),
});
export type ActivateWeeklyPlanRequest = z.infer<typeof activateWeeklyPlanRequestSchema>;

export const discardWeeklyPlanRequestSchema = z.object({
  expectedRevision: z.number().int().positive(),
});
export type DiscardWeeklyPlanRequest = z.infer<typeof discardWeeklyPlanRequestSchema>;

export const weeklyPlanSchema = z.object({
  id: z.string().uuid(),
  weekStart: dateSchema,
  weekEnd: dateSchema,
  title: z.string(),
  note: z.string().nullable(),
  dayStartsAt: weeklyPlanDayStartSchema,
  executionMode: weeklyPlanExecutionModeSchema.default("CAPACITY_SCHEDULED"),
  status: weeklyPlanStatusSchema,
  revision: z.number().int().positive(),
  rows: z.array(weeklyPlanRowSchema),
  createdBy: z.string(),
  createdAt: instantSchema,
  updatedBy: z.string(),
  updatedAt: instantSchema,
  activatedBy: z.string().nullable(),
  activatedAt: instantSchema.nullable(),
  savedProductionPlanId: z.string().uuid().nullable(),
  sourceFile: z.object({
    fileName: z.string(),
    fileHash: z.string().regex(/^[a-f0-9]{64}$/u),
    importedAt: instantSchema,
  }).nullable().default(null),
});
export type WeeklyPlan = z.infer<typeof weeklyPlanSchema>;

export const weeklyPlanSummarySchema = weeklyPlanSchema.omit({ rows: true }).extend({
  rowCount: z.number().int().nonnegative(),
  totalPlannedHours: z.number().nonnegative(),
});
export type WeeklyPlanSummary = z.infer<typeof weeklyPlanSummarySchema>;

export const weeklyPlanningLineSchema = z.object({
  id: z.string().min(1).max(120),
  code: z.string(),
  name: z.string(),
  active: z.boolean(),
});
export type WeeklyPlanningLine = z.infer<typeof weeklyPlanningLineSchema>;

export const weeklyPlanCarryoverCandidateSchema = z.object({
  sourceRowId: z.string().uuid(),
  sourcePlanId: z.string().uuid(),
  sourceWeekStart: dateSchema,
  lineId: z.string().min(1).max(120),
  lineName: z.string(),
  workOrderNo: z.string().nullable(),
  productCode: z.string(),
  productName: z.string(),
  remainingQuantity: z.number().positive(),
  unit: weeklyPlanUnitSchema.nullable(),
  capacityPerHour: z.number().positive().nullable(),
  executionStatus: z.enum(["READY", "ACTIVE", "COMPLETED", "NOT_STARTED"]),
  canCarryOver: z.boolean(),
  blockedReason: z.string().nullable(),
});
export type WeeklyPlanCarryoverCandidate = z.infer<typeof weeklyPlanCarryoverCandidateSchema>;

export const weeklyPlanningReferenceDataResponseSchema = z.object({ lines: z.array(weeklyPlanningLineSchema) });
export type WeeklyPlanningReferenceDataResponse = z.infer<typeof weeklyPlanningReferenceDataResponseSchema>;
export const weeklyPlanListResponseSchema = z.object({ items: z.array(weeklyPlanSummarySchema) });
export type WeeklyPlanListResponse = z.infer<typeof weeklyPlanListResponseSchema>;
export const weeklyPlanDetailResponseSchema = z.object({ plan: weeklyPlanSchema });
export type WeeklyPlanDetailResponse = z.infer<typeof weeklyPlanDetailResponseSchema>;
export const weeklyPlanCarryoverResponseSchema = z.object({ items: z.array(weeklyPlanCarryoverCandidateSchema) });
export type WeeklyPlanCarryoverResponse = z.infer<typeof weeklyPlanCarryoverResponseSchema>;
export const weeklyPlanActivationResponseSchema = z.object({
  plan: weeklyPlanSchema,
  activation: z.object({
    id: z.string().uuid(),
    idempotent: z.boolean(),
    activatedAt: instantSchema,
    productionRunCount: z.number().int().nonnegative(),
  }),
});
export type WeeklyPlanActivationResponse = z.infer<typeof weeklyPlanActivationResponseSchema>;

const comparisonRowSchema = z.object({
  rowId: z.string().min(1).max(120),
  sourceRow: z.number().int().positive().nullable(),
  lineName: z.string(),
  sequence: z.number().int().positive(),
  workOrderNo: z.string().nullable(),
  productCode: z.string().nullable(),
  productName: z.string(),
  kind: weeklyPlanRowKindSchema,
  quantity: z.number().nullable(),
  unit: weeklyPlanUnitSchema.nullable(),
  capacityPerHour: z.number().nullable(),
  plannedHours: z.number().nullable(),
  scheduledHours: z.number().nullable(),
  scheduledQuantity: z.number().nullable(),
  deferredQuantity: z.number().nullable(),
  scheduledStartAt: instantSchema.nullable(),
  scheduledEndAt: instantSchema.nullable(),
  allocations: z.array(z.object({
    requiredAt: dateSchema,
    quantity: z.number().nonnegative(),
    plannedHours: z.number().positive(),
    startOffsetHours: z.number().nonnegative(),
  })),
  note: z.string().nullable(),
  state: weeklyPlanRowStateSchema,
});

export const weeklyPlanActivationPreviewSchema = z.object({
  planId: z.string().uuid(),
  revision: z.number().int().positive(),
  executionMode: weeklyPlanExecutionModeSchema,
  digest: z.string().regex(/^[a-f0-9]{64}$/u),
  baseline: z.object({ planId: z.string().uuid().nullable(), rowCount: z.number().int().nonnegative() }),
  calendar: z.object({
    baselineDayStartsAt: weeklyPlanDayStartSchema.nullable(),
    sourceDayStartsAt: weeklyPlanDayStartSchema.nullable(),
    draftDayStartsAt: weeklyPlanDayStartSchema,
  }),
  draft: z.object({ rowCount: z.number().int().nonnegative(), includedRowCount: z.number().int().nonnegative(), excludedRowCount: z.number().int().nonnegative() }),
  changes: z.array(z.object({
    type: z.enum(["ADDED", "REMOVED", "CHANGED"]),
    before: comparisonRowSchema.nullable(),
    after: comparisonRowSchema.nullable(),
  })),
  sourceChanges: z.array(z.object({
    before: comparisonRowSchema,
    after: comparisonRowSchema,
  })),
  includedRows: z.array(comparisonRowSchema),
  excludedRows: z.array(comparisonRowSchema.extend({ reasons: z.array(z.string()) })),
});
export type WeeklyPlanActivationPreview = z.infer<typeof weeklyPlanActivationPreviewSchema>;

export const weeklyPlanImportResponseSchema = z.object({
  plan: weeklyPlanSchema,
  importSummary: z.object({
    sourceRowCount: z.number().int().nonnegative(),
    readyRowCount: z.number().int().nonnegative(),
    incompleteRowCount: z.number().int().nonnegative(),
    dependencyBlockedRowCount: z.number().int().nonnegative(),
    issueCount: z.number().int().nonnegative(),
    readOnlyUnitLookup: z.enum(["AVAILABLE", "UNAVAILABLE"]),
  }),
});
export type WeeklyPlanImportResponse = z.infer<typeof weeklyPlanImportResponseSchema>;
